import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Plus, Pencil, Trash2, Check, Users, Clock } from 'lucide-react';
import api from '../../utils/api';
import Modal, { ConfirmModal, Field } from '../../components/Modal';
import { formatINR, errMsg } from './saUtils';

const EMPTY = { name: '', description: '', price: '', duration_days: 365, max_users: '', features: '', is_active: 1 };

export default function SuperAdminPackagesPage() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // null = closed
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = () => {
    setLoading(true);
    api.get('/superadmin/packages')
      .then(res => setPackages(res.data))
      .catch(err => toast.error(errMsg(err, 'Could not load packages')))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openEdit = (p) => setForm({
    ...p,
    price: p.price ?? '',
    max_users: p.max_users ?? '',
    features: (p.features || []).join('\n'),
  });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      description: form.description,
      price: form.price === '' ? 0 : Number(form.price),
      duration_days: Number(form.duration_days),
      max_users: form.max_users === '' ? null : Number(form.max_users),
      features: String(form.features || '').split('\n').map(s => s.trim()).filter(Boolean),
      is_active: form.is_active ? 1 : 0,
    };
    try {
      if (form.id) await api.put(`/superadmin/packages/${form.id}`, payload);
      else await api.post('/superadmin/packages', payload);
      toast.success(form.id ? 'Package updated' : 'Package created');
      setForm(null);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    try {
      await api.delete(`/superadmin/packages/${toDelete.id}`);
      toast.success('Package deleted');
      setToDelete(null);
      load();
    } catch (err) { toast.error(errMsg(err)); setToDelete(null); }
  };

  return (
    <div className="space-y-5 max-w-7xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Packages</h1>
          <p className="text-sm text-slate-500">Subscription plans — price, duration, user limit</p>
        </div>
        <button className="btn-primary" onClick={() => setForm({ ...EMPTY })}><Plus size={16} /> New package</button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" /></div>
      ) : packages.length === 0 ? (
        <div className="card p-10 text-center">
          <p className="font-semibold text-slate-800">No packages yet</p>
          <p className="text-sm text-slate-500 mt-1">Create your first package (e.g. Basic / Standard / Premium) and assign it to organizations.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {packages.map(p => (
            <div key={p.id} className={`card p-5 flex flex-col ${p.is_active ? '' : 'opacity-60'}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-bold text-slate-900 text-lg truncate">{p.name}</h3>
                  {!p.is_active && <span className="text-xs font-semibold text-slate-500">Inactive</span>}
                </div>
                <div className="flex gap-1">
                  <button className="btn-ghost !p-2" title="Edit" onClick={() => openEdit(p)}><Pencil size={15} /></button>
                  <button className="btn-ghost !p-2 hover:!text-red-600" title="Delete" onClick={() => setToDelete(p)}><Trash2 size={15} /></button>
                </div>
              </div>
              <p className="text-2xl font-bold text-slate-900 mt-2">{formatINR(p.price)}</p>
              <div className="flex flex-wrap gap-3 text-xs text-slate-600 mt-2">
                <span className="flex items-center gap-1"><Clock size={13} /> {p.duration_days} days</span>
                <span className="flex items-center gap-1"><Users size={13} /> {p.max_users ? `${p.max_users} users` : 'Unlimited users'}</span>
              </div>
              {p.description && <p className="text-sm text-slate-500 mt-3">{p.description}</p>}
              {p.features?.length > 0 && (
                <ul className="mt-3 space-y-1.5">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <Check size={15} className="text-emerald-600 mt-0.5 flex-shrink-0" /> <span>{f}</span>
                    </li>
                  ))}
                </ul>
              )}
              <p className="text-xs text-slate-500 mt-auto pt-4">{p.org_count} organization(s) on this package</p>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={!!form}
        onClose={() => setForm(null)}
        title={form?.id ? 'Edit package' : 'New package'}
        size="md"
        footer={<>
          <button type="button" className="btn-secondary" onClick={() => setForm(null)}>Cancel</button>
          <button type="submit" form="pkg-form" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </>}
      >
        {form && (
          <form id="pkg-form" onSubmit={save}>
            <Field label="Package name" required>
              <input className="field-input" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Standard" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Price (₹)">
                <input className="field-input" type="number" min="0" step="0.01" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} />
              </Field>
              <Field label="Duration (days)" required>
                <input className="field-input" type="number" min="1" required value={form.duration_days} onChange={e => setForm({ ...form, duration_days: e.target.value })} />
              </Field>
            </div>
            <Field label="Max users" hint="Leave empty = unlimited. Admins + workers are counted.">
              <input className="field-input" type="number" min="1" value={form.max_users} onChange={e => setForm({ ...form, max_users: e.target.value })} />
            </Field>
            <Field label="Description">
              <input className="field-input" value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} />
            </Field>
            <Field label="Features" hint="One feature per line">
              <textarea className="field-input" rows={4} value={form.features} onChange={e => setForm({ ...form, features: e.target.value })} placeholder={'Projects & Tasks\nPacking & Dispatch\nClient Portal'} />
            </Field>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={!!form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked ? 1 : 0 })} />
              Active (available to assign)
            </label>
          </form>
        )}
      </Modal>

      <ConfirmModal
        open={!!toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={remove}
        danger
        title="Delete package?"
        message={toDelete ? `"${toDelete.name}" will be permanently deleted.` : ''}
        confirmLabel="Delete"
      />
    </div>
  );
}
